import {useQuery} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    AlertTriangle,
    ArrowUpRight,
    Building2,
    CircleDollarSign,
    CreditCard,
    FileCheck2,
    Inbox,
    Landmark,
    Network,
    ReceiptText,
    Users,
    WalletCards,
} from "lucide-react";
import {ChartTooltip} from "../../shared/components/ChartTooltip";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {useTranslation} from "../../shared/i18n";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {WALLET_BUCKETS} from "../../shared/wallets";
import {loadOverview} from "./api";
import {buildOperationalIssueSeries} from "./contracts";
import {DeployerOverviewPage} from "./DeployerOverviewPage";
import {PartnerOverviewPage} from "./PartnerOverviewPage";

const BUCKET_SOURCES = {
    main: "mainBalance",
    plus: "plusBalance",
    extra: "extraBalance",
    frozen: "frozenBalance",
};

const moneyBuckets = (overview, t) =>
    WALLET_BUCKETS.map((bucket) => ({
        name: t(`overview.buckets.${bucket.key}`),
        value: Number(overview?.users?.[BUCKET_SOURCES[bucket.key]] || 0),
        color: bucket.color,
    }));

const formatMoney = (value) =>
    new Intl.NumberFormat("ru-RU", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value || 0));

const compactMoney = (value) =>
    new Intl.NumberFormat("ru-RU", {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(value || 0));

const getTotalLiability = (overview) =>
    WALLET_BUCKETS.reduce(
        (total, bucket) => total + Number(overview?.users?.[BUCKET_SOURCES[bucket.key]] || 0),
        0,
    );

const MetricCard = ({title, value, note, icon: Icon, tone = "default", connected = true}) => {
    const {t} = useTranslation();
    return (
        <article className="admin-card admin-metric-card admin-card-interactive">
            <div className="admin-metric-topline">
                <span>{title}</span>
                {Icon && <Icon size={17} />}
            </div>
            <p className={`admin-card-value ${tone === "money" ? "is-money" : ""}`}>
                {connected ? value : "—"}
            </p>
            <p className="admin-card-note">{connected ? note : t("overview.noAccess")}</p>
        </article>
    );
};

const OperationSignal = ({title, value, note, href, icon: Icon, tone = "neutral", connected = true}) => {
    const {t} = useTranslation();
    const content = (
        <>
            <span className={`admin-operation-signal-icon is-${tone}`}>
                <Icon size={16} />
            </span>
            <div>
                <strong>{title}</strong>
                <em>{connected ? note : t("overview.unavailable")}</em>
            </div>
            <b>{connected ? value : "—"}</b>
        </>
    );

    if (!href) {
        return <div className="admin-operation-signal">{content}</div>;
    }

    return (
        <Link className="admin-operation-signal" to={href}>
            {content}
        </Link>
    );
};

const PlatformOverviewPage = () => {
    const {t} = useTranslation();
    const {data, isLoading, isError} = useQuery({
        queryKey: ["admin-overview"],
        queryFn: loadOverview,
    });

    const bucketData = moneyBuckets(data, t);
    const totalLiability = getTotalLiability(data);
    const connectedEndpoints = [
        data?.organizations?.connected,
        data?.users?.connected,
        data?.settlements?.connected,
        data?.inbox?.connected,
    ].filter(Boolean).length;
    const readiness = Math.round((connectedEndpoints / 4) * 100);
    const settlementDrafts = Number(data?.settlements?.draftCount || 0);
    const settlementPaid = Number(data?.settlements?.paidCount || 0);
    const activeQueue = Number(data?.inbox?.notifications || 0) + settlementDrafts;
    const placeOperations = data?.operations?.places || {};
    const contentOperations = data?.operations?.content || {};
    const notificationOperations = data?.operations?.notifications || {};
    const placeIssueCount =
        Number(placeOperations.missingLegal || 0) +
        Number(placeOperations.missingMedia || 0) +
        Number(placeOperations.branchesWithoutGallery || 0) +
        Number(placeOperations.branchesWithoutStaff || 0);
    const contentIssueCount =
        Number(contentOperations.bannersWithoutContent || 0) +
        Number(contentOperations.storiesWithoutPreview || 0) +
        Number(contentOperations.storiesWithoutItems || 0) +
        Number(contentOperations.inactiveBanners || 0) +
        Number(contentOperations.inactiveStories || 0);
    const notificationIssueCount =
        Number(notificationOperations.pendingCount || 0) +
        Number(notificationOperations.changedCount || 0) +
        Number(notificationOperations.rejectedCount || 0);
    const operationalSignals = [
        {
            title: t("overview.signals.settlements"),
            value: settlementDrafts,
            note: t("overview.draftsPaid", {drafts: settlementDrafts, paid: settlementPaid}),
            href: "/settlements",
            icon: Landmark,
            tone: settlementDrafts > 0 ? "warning" : "success",
            connected: data?.settlements?.connected,
        },
        {
            title: t("overview.signals.places"),
            value: placeIssueCount,
            note: t("overview.signals.placesNote", {
                legal: placeOperations.missingLegal || 0,
                staff: placeOperations.branchesWithoutStaff || 0,
            }),
            href: "/employers",
            icon: Building2,
            tone: placeIssueCount > 0 ? "warning" : "success",
            connected: placeOperations.connected,
        },
        {
            title: t("overview.signals.content"),
            value: contentIssueCount,
            note: t("overview.signals.contentNote", {
                banners: contentOperations.bannersWithoutContent || 0,
                stories: contentOperations.storiesWithoutItems || 0,
            }),
            href: "/news",
            icon: ReceiptText,
            tone: contentIssueCount > 0 ? "warning" : "success",
            connected: contentOperations.connected,
        },
        {
            title: t("overview.signals.inbox"),
            value: notificationIssueCount,
            note: t("overview.signals.inboxNote", {
                pending: notificationOperations.pendingCount || 0,
                changed: notificationOperations.changedCount || 0,
            }),
            href: "/notifications",
            icon: Inbox,
            tone: notificationIssueCount > 0 ? "danger" : "success",
            connected: notificationOperations.connected,
        },
    ];
    const openSignalCount = operationalSignals.reduce(
        (total, item) => total + (item.connected ? Number(item.value || 0) : 0),
        0,
    );
    const operationalIssueData = buildOperationalIssueSeries(data).map((row) => ({
        ...row,
        name: t(`overview.series.${row.id}`),
    }));

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("overview.title")}</h1>
                    <p className="admin-page-copy">{t("overview.copy")}</p>
                </div>
                {isLoading && <div className="admin-pill"><ArrowUpRight size={14} /> {t("overview.refreshing")}</div>}
                {isError && <div className="admin-pill admin-pill-danger"><AlertTriangle size={14} /> {t("overview.needsCheck")}</div>}
            </header>

            <section className="admin-overview-hero">
                <article className="admin-overview-hero-main">
                    <div>
                        <p className="admin-eyebrow">{t("overview.liabilityEyebrow")}</p>
                        <h2>{formatMoney(totalLiability)}</h2>
                        <p>{t("overview.liabilityCopy")}</p>
                    </div>
                    <div className="admin-hero-ledger">
                        <div className="admin-ledger-line">
                            <span>{t("overview.backendData")}</span>
                            <strong>{readiness}%</strong>
                        </div>
                        <div className="admin-progress-track">
                            <span style={{width: `${readiness}%`}} />
                        </div>
                        <div className="admin-ledger-line">
                            <span>{t("overview.openQueue")}</span>
                            <strong>{formatMoney(activeQueue)}</strong>
                        </div>
                    </div>
                </article>

                <article className="admin-card admin-ledger-board">
                    <div className="admin-ledger-board-header">
                        <p className="admin-card-title"><FileCheck2 size={14} /> {t("overview.ledgerTitle")}</p>
                        <span>{t("overview.inOperation", {count: connectedEndpoints})}</span>
                    </div>
                    <div className="admin-ledger-rows">
                        <div><span>{t("overview.wallets")}</span><strong>{data?.users?.connected ? t("overview.synced") : t("overview.noPermission")}</strong></div>
                        <div><span>{t("overview.places")}</span><strong>{data?.organizations?.connected ? t("overview.synced") : t("overview.noPermission")}</strong></div>
                        <div><span>{t("overview.settlements")}</span><strong>{data?.settlements?.connected ? t("overview.drafts", {count: settlementDrafts}) : t("overview.noPermission")}</strong></div>
                        <div><span>{t("overview.inbox")}</span><strong>{data?.inbox?.connected ? t("overview.openCount", {count: data?.inbox?.notifications || 0}) : t("overview.noPermission")}</strong></div>
                    </div>
                </article>
            </section>

            <section className="admin-grid cols-4">
                <MetricCard
                    title={t("overview.places")}
                    value={formatMoney(data?.organizations?.count)}
                    note={t("overview.placesNote")}
                    icon={Building2}
                    connected={data?.organizations?.connected}
                />
                <MetricCard
                    title={t("overview.users")}
                    value={formatMoney(data?.users?.count)}
                    note={t("overview.usersNote")}
                    icon={Users}
                    connected={data?.users?.connected}
                />
                <MetricCard
                    title={t("overview.settlements")}
                    value={formatMoney(data?.settlements?.count)}
                    note={t("overview.draftsPaid", {drafts: settlementDrafts, paid: settlementPaid})}
                    icon={Landmark}
                    connected={data?.settlements?.connected}
                />
                <MetricCard
                    title={t("overview.inbox")}
                    value={formatMoney(data?.inbox?.notifications)}
                    note={t("overview.inboxNote")}
                    icon={Inbox}
                    connected={data?.inbox?.connected}
                />
            </section>

            <section className="admin-status-strip">
                <div className="admin-status-item">
                    <CreditCard size={16} />
                    <span>Airba</span>
                    <strong>{t("shell.country.names.kz")}</strong>
                </div>
                <div className="admin-status-item">
                    <CircleDollarSign size={16} />
                    <span>Bakai</span>
                    <strong>{t("shell.country.names.kg")}</strong>
                </div>
                <div className="admin-status-item">
                    <ReceiptText size={16} />
                    <span>{t("overview.postings")}</span>
                    <strong>{t("overview.ledgerFirst")}</strong>
                </div>
                <div className="admin-status-item">
                    <Network size={16} />
                    <span>{t("overview.referrals")}</span>
                    <strong>{t("overview.levels")}</strong>
                </div>
            </section>

            <section className="admin-grid cols-2">
                <article className="admin-card admin-chart-card">
                    <div className="admin-chart-header">
                        <div>
                            <p className="admin-card-title"><WalletCards size={14} /> {t("overview.balances")}</p>
                            <p className="admin-card-note">{t("overview.balancesNote")}</p>
                        </div>
                        <strong>{compactMoney(totalLiability)}</strong>
                    </div>
                    <div style={{height: 230, marginTop: 18}}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={bucketData}>
                                <CartesianGrid stroke="rgba(214,229,222,0.08)" vertical={false} />
                                <XAxis dataKey="name" stroke="#8ea0ad" fontSize={11} tickLine={false} axisLine={false} />
                                <YAxis
                                    stroke="#8ea0ad"
                                    fontSize={11}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={compactMoney}
                                    width={48}
                                />
                                <Tooltip
                                    cursor={{fill: "rgba(87, 215, 167, 0.045)"}}
                                    content={<ChartTooltip formatter={formatMoney} valueLabel={t("overview.balance")} />}
                                />
                                <Bar dataKey="value" name={t("overview.balance")} radius={[6, 6, 0, 0]}>
                                    {bucketData.map((entry) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </article>

                <article className="admin-card admin-chart-card">
                    <div className="admin-chart-header">
                        <div>
                            <p className="admin-card-title"><CircleDollarSign size={14} /> {t("overview.load")}</p>
                            <p className="admin-card-note">{t("overview.loadNote")}</p>
                        </div>
                        <strong>{formatMoney(openSignalCount)}</strong>
                    </div>
                    <div style={{height: 230, marginTop: 18}}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={operationalIssueData}>
                                <defs>
                                    <linearGradient id="health" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#57d7a7" stopOpacity={0.48} />
                                        <stop offset="95%" stopColor="#57d7a7" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(214,229,222,0.08)" vertical={false} />
                                <XAxis dataKey="name" stroke="#8ea0ad" fontSize={11} tickLine={false} axisLine={false} />
                                <YAxis
                                    stroke="#8ea0ad"
                                    fontSize={11}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={formatMoney}
                                    width={48}
                                />
                                <Tooltip
                                    cursor={{stroke: "rgba(87, 215, 167, 0.22)", strokeWidth: 1}}
                                    content={<ChartTooltip formatter={formatMoney} valueLabel={t("overview.open")} />}
                                />
                                <Area type="monotone" dataKey="value" name={t("overview.open")} stroke="#57d7a7" fill="url(#health)" connectNulls={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </article>
            </section>

            <section className="admin-operations-control">
                <article className="admin-card admin-operations-control-main">
                    <div className="admin-chart-header">
                        <div>
                            <p className="admin-card-title"><AlertTriangle size={14} /> {t("overview.control")}</p>
                            <p className="admin-card-note">{t("overview.controlNote")}</p>
                        </div>
                        <strong>{formatMoney(openSignalCount)}</strong>
                    </div>
                    <div className="admin-operation-signals">
                        {operationalSignals.map((item) => (
                            <OperationSignal key={item.title} {...item} />
                        ))}
                    </div>
                </article>

                <article className="admin-card admin-operations-control-side">
                    <p className="admin-card-title"><CreditCard size={14} /> {t("overview.moneyContour")}</p>
                    <div className="admin-contour-list">
                        <div>
                            <span>Airba</span>
                            <strong>{t("shell.country.names.kz")}</strong>
                            <em>{t("overview.cards")}</em>
                        </div>
                        <div>
                            <span>Bakai</span>
                            <strong>{t("shell.country.names.kg")}</strong>
                            <em>{t("overview.topups")}</em>
                        </div>
                        <div>
                            <span>Ledger</span>
                            <strong>{t("overview.percentInOperation", {count: readiness})}</strong>
                            <em>{t("overview.ledgerScope")}</em>
                        </div>
                    </div>
                </article>
            </section>
        </div>
    );
};

export const OverviewPage = () => {
    const capabilities = useCapabilities();
    const role = capabilities.user?.role;

    if (role === ADMIN_ROLES.OWNER) {
        return <PartnerOverviewPage />;
    }

    if (role === ADMIN_ROLES.DEPLOYER || role === ADMIN_ROLES.IMPLEMENTATION_HEAD) {
        return <DeployerOverviewPage />;
    }

    return <PlatformOverviewPage />;
};
